// Utilitaires spécialisés pour la synchronisation cross-device des pending nodes
import { saveSessionData, loadSessionData } from './sessionStorageClient';

// Configuration
const PENDING_NODES_KEY = 'pendingNodes';
const DEBOUNCE_DELAY = 500; // ms

// Cache pour éviter les appels répétés
let saveTimeout = null;

/**
 * Sauvegarder les pending nodes avec debounce pour éviter trop de requêtes
 * @param {Array} pendingNodes - Tableau des pending nodes
 * @returns {Promise<void>}
 */
export async function savePendingNodes(pendingNodes) {
  // Éviter de sauvegarder un tableau vide au démarrage
  if (!Array.isArray(pendingNodes) || pendingNodes.length === 0) {
    return;
  }

  // Debounce pour éviter trop de requêtes
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(async () => {
    try {
      // 1. Local immédiat pour performance
      localStorage.setItem(PENDING_NODES_KEY, JSON.stringify(pendingNodes));
      
      // 2. Session cross-device pour persistance
      await saveSessionData(PENDING_NODES_KEY, pendingNodes);
      
      console.log(`✅ ${pendingNodes.length} pending nodes sauvés en local ET session cross-device`);
    } catch (error) {
      console.warn('❌ Erreur sauvegarde pending nodes:', error.message);
    }
  }, DEBOUNCE_DELAY);
}

/**
 * Charger les pending nodes avec priorité session > localStorage
 * @returns {Promise<Array>} - Tableau des pending nodes
 */
export async function loadPendingNodes() {
  try {
    // 1. Essayer la session d'abord (source de vérité cross-device)
    const sessionData = await loadSessionData(PENDING_NODES_KEY);
    if (sessionData && Array.isArray(sessionData) && sessionData.length > 0) {
      // Synchroniser le local avec la session
      localStorage.setItem(PENDING_NODES_KEY, JSON.stringify(sessionData));
      console.log(`✅ ${sessionData.length} pending nodes chargés depuis session cross-device`);
      return sessionData;
    }
    
    // 2. Fallback sur localStorage si session vide/indisponible
    const localData = localStorage.getItem(PENDING_NODES_KEY);
    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Pousser vers session pour sync future (sans attendre)
          saveSessionData(PENDING_NODES_KEY, parsed).catch(() => {
            // Ignorer les erreurs de sync silencieusement
          });
          console.log(`📱 ${parsed.length} pending nodes chargés depuis localStorage + sync session`);
          return parsed;
        }
      } catch (e) {
        console.warn('❌ Erreur parsing localStorage pendingNodes:', e);
        localStorage.removeItem(PENDING_NODES_KEY);
      }
    }
    
    // 3. Aucune donnée trouvée
    console.log('📭 Aucun pending node trouvé');
    return [];
  } catch (error) {
    console.error('❌ Erreur chargement pending nodes:', error);
    return [];
  }
}

/**
 * Forcer la synchronisation immédiate (utile après import/export)
 * @param {Array} pendingNodes - Tableau des pending nodes
 * @returns {Promise<void>}
 */
export async function forceSyncPendingNodes(pendingNodes) {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }
  
  try {
    localStorage.setItem(PENDING_NODES_KEY, JSON.stringify(pendingNodes));
    await saveSessionData(PENDING_NODES_KEY, pendingNodes);
    console.log(`🔄 ${pendingNodes.length} pending nodes synchronisés immédiatement`);
  } catch (error) {
    console.error('❌ Erreur sync forcée pending nodes:', error);
    throw error;
  }
}

/**
 * Vérifier si des pending nodes sont disponibles en session (sans les charger)
 * @returns {Promise<boolean>}
 */
export async function hasPendingNodesInSession() {
  try {
    const sessionData = await loadSessionData(PENDING_NODES_KEY);
    return sessionData && Array.isArray(sessionData) && sessionData.length > 0;
  } catch {
    return false;
  }
}

/**
 * Obtenir des statistiques sur les pending nodes sans les charger entièrement
 * @returns {Promise<Object>} - Objet avec count, lastActivity, etc.
 */
export async function getPendingNodesStats() {
  try {
    // Essayer d'utiliser les métadonnées enrichies du serveur si disponibles
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/session-data/pendingNodes`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data)) {
        return {
          count: data.length,
          hasData: data.length > 0,
          types: data.reduce((acc, node) => {
            const type = node.type || 'Unknown';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {}),
          source: 'session'
        };
      }
    }
    
    // Fallback sur localStorage
    const localData = localStorage.getItem(PENDING_NODES_KEY);
    if (localData) {
      const parsed = JSON.parse(localData);
      if (Array.isArray(parsed)) {
        return {
          count: parsed.length,
          hasData: parsed.length > 0,
          types: parsed.reduce((acc, node) => {
            const type = node.type || 'Unknown';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {}),
          source: 'localStorage'
        };
      }
    }
    
    return { count: 0, hasData: false, types: {}, source: 'none' };
  } catch (error) {
    console.error('❌ Erreur stats pending nodes:', error);
    return { count: 0, hasData: false, types: {}, source: 'error' };
  }
}